import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import storefrontService from 'services/storefrontService';
import { FeatureFlags, NavigationConfig, PageLayout, ThemeConfig } from 'types/storefront';
import { PaletteMode } from '@mui/material';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { tenantStorage } from 'utils/tenantStorage';

interface StorefrontContextValue {
  theme: ThemeConfig | null;
  navigation: NavigationConfig;
  features: FeatureFlags;
  colorMode: PaletteMode;
  setColorMode: (mode: PaletteMode) => void;
  getLayout: (page: string) => Promise<PageLayout>;
  loading: boolean;
}

const defaultNavigation: NavigationConfig = { header: [], footer: [] };
const defaultFeatures: FeatureFlags = {
  promotionsEnabled: true,
  brandsEnabled: true,
  b2bPriceListsEnabled: true,
};

const StorefrontContext = createContext<StorefrontContextValue | undefined>(undefined);

export const StorefrontProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeConfig | null>(null);
  const [navigation, setNavigation] = useState<NavigationConfig>(defaultNavigation);
  const [features, setFeatures] = useState<FeatureFlags>(defaultFeatures);
  const [loading, setLoading] = useState(true);
  const [colorMode, setColorModeState] = useState<PaletteMode>(
    () => (tenantStorage.getItem(STORAGE_KEYS.COLOR_MODE) as PaletteMode) || 'light'
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [themeData, navData, featuresData] = await Promise.all([
          storefrontService.getTheme(),
          storefrontService.getNavigation(),
          storefrontService.getFeatures(),
        ]);
        setTheme(themeData);
        setNavigation(navData);
        setFeatures(featuresData);
        const persistedColorMode = tenantStorage.getItem(STORAGE_KEYS.COLOR_MODE);
        if (!persistedColorMode && themeData.defaultColorMode) {
          setColorModeState(themeData.defaultColorMode);
        }
      } catch (error) {
        console.error('Error loading storefront config:', error);
        setNavigation(defaultNavigation);
        setFeatures(defaultFeatures);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const setColorMode = (mode: PaletteMode) => {
    tenantStorage.setItem(STORAGE_KEYS.COLOR_MODE, mode);
    setColorModeState(mode);
  };

  const getLayout = useCallback((page: string) => storefrontService.getLayout(page), []);

  const value = useMemo(
    () => ({ theme, navigation, features, colorMode, setColorMode, getLayout, loading }),
    [theme, navigation, features, colorMode, getLayout, loading]
  );

  return <StorefrontContext.Provider value={value}>{children}</StorefrontContext.Provider>;
};

export const useStorefrontContext = () => {
  const ctx = useContext(StorefrontContext);
  if (!ctx) throw new Error('useStorefrontContext debe usarse dentro de StorefrontProvider');
  return ctx;
};
